import { useCallback, useState } from "react";

import isNil from "lodash/isNil";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useAppSelector } from "@/store/hooks";
import {
  selectAppointmentTypeId,
  selectAppointmentTypes,
  useBookingDispatch,
} from "@/store/reducers/booking";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import BookingOptionCard from "@/ui/components/schedule/booking/BookingOptionCard";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { FlatList } from "@/ui/layout";

const AppointmentDurationStep = ({ step }: BaseBookingStepProps) => {
  const { setAppointmentTypeId } = useBookingDispatch();
  const { onBackwardNavigation, onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });
  const allAppointmentTypes = useAppSelector(selectAppointmentTypes);
  const selectedSessionTypeId = useAppSelector(selectAppointmentTypeId);
  const baseTestID = BookingStepBaseTestIDMap[step];
  const { trackEvent } = useBookingAnalytics();

  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    string | undefined
  >(selectedSessionTypeId);

  // Filter appointment types based on the selected session type
  const getFilteredAppointmentTypes = () => {
    if (isNil(allAppointmentTypes) || isNil(selectedSessionTypeId)) {
      return allAppointmentTypes || [];
    }

    const selectedSessionType = allAppointmentTypes.find(
      (type) => type.appointmentTypeExtId === selectedSessionTypeId,
    );

    if (isNil(selectedSessionType)) {
      return allAppointmentTypes;
    }
    return allAppointmentTypes.filter(
      (type) => type.appointmentType === selectedSessionType.appointmentType,
    );
  };

  const appointmentTypes = getFilteredAppointmentTypes();

  const handleForwardNavigation = () => {
    if (isNil(selectedAppointmentId)) {
      return;
    }

    setAppointmentTypeId(selectedAppointmentId);
    onForwardNavigation();
  };
  const ListHeaderComponent = useCallback(
    () => <BookingStepTitle step={step} />,
    [step],
  );

  return (
    <>
      <FlatList
        data={appointmentTypes}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const isSelected =
            selectedAppointmentId === item.appointmentTypeExtId;
          const durationLabel = `${item.durationInMinutes} minutes`;

          const handleAppointmentTypeSelect = () => {
            if (item.appointmentTypeExtId !== selectedAppointmentId) {
              trackEvent({
                event: BookingAnalyticsEvent.DurationSelection,
                source: step,
                value: durationLabel,
              });
            }
            setSelectedAppointmentId(item.appointmentTypeExtId);
          };

          return (
            <BookingOptionCard
              id={item.appointmentTypeExtId}
              isSelected={isSelected}
              onSelect={handleAppointmentTypeSelect}
              title={durationLabel}
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        isForwardNavigationDisabled={isNil(selectedAppointmentId)}
        onBackwardPress={onBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentDurationStep;
